import React, { useEffect, useState } from 'react';
import { Box, Chip, Typography, FormHelperText, CircularProgress, Alert, Stack, Button } from '@mui/material';
import api from '../../../services/api';

/**
 * Multi-select chips for the voice whitelist at service-group level.
 *
 * Voices are configured separately from persona representations: a
 * representation bundles a suggested avatar + voice, but the voices a client
 * may actually be offered are their own list, curated from the VoiceCatalog.
 * This narrows that list for one service group.
 *
 * Empty = no narrowing, inherit whatever the platform allows.
 *
 * A group whitelist can only narrow, never widen — clientUiResolver intersects
 * with the platform list. So voices outside the platform range are shown
 * greyed and cannot be selected: picking one would look like it worked and
 * then resolve to nothing.
 */
const VoiceWhitelist = ({ value = [], onChange, platformVoiceIds = null, helperText = '' }) => {
  const [voices, setVoices] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    api.get('/admin/voice-library/voices')
      .then((res) => {
        if (cancelled) return;
        setVoices(Array.isArray(res.data?.voices) ? res.data.voices : []);
      })
      .catch((e) => {
        if (cancelled) return;
        setError(e.response?.data?.error || 'Failed to load the voice catalogue');
      })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, []);

  const selected = (value || []).map(String);
  // null means "platform has no explicit list", i.e. everything is in range.
  const platformSet = Array.isArray(platformVoiceIds) && platformVoiceIds.length
    ? new Set(platformVoiceIds.map(String))
    : null;

  const inPlatformRange = (id) => !platformSet || platformSet.has(String(id));

  const toggle = (id) => {
    const key = String(id);
    if (!inPlatformRange(key)) return;
    onChange(selected.includes(key) ? selected.filter(v => v !== key) : [...selected, key]);
  };

  if (loading) return <CircularProgress size={20} />;
  if (error) return <Alert severity="warning" sx={{ my: 1 }}>{error}</Alert>;
  if (!voices.length) {
    return (
      <Alert severity="info" sx={{ my: 1 }}>
        No voices in the catalogue yet. Add them under Voice &amp; Avatar in admin first.
      </Alert>
    );
  }

  const outOfRange = voices.filter(v => !inPlatformRange(v._id)).length;

  return (
    <Box sx={{ my: 1 }}>
      <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }}>
        <Typography variant="body2">Voices offered in this service group</Typography>
        {selected.length > 0 && (
          <Button size="small" onClick={() => onChange([])}>Clear (inherit all)</Button>
        )}
      </Stack>
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
        {voices.map((v) => {
          const id = String(v._id);
          const allowed = inPlatformRange(id);
          const isOn = selected.includes(id);
          return (
            <Chip
              key={id}
              label={v.name + (v.tags?.gender ? ` · ${v.tags.gender}` : '')}
              onClick={() => toggle(id)}
              color={isOn ? 'primary' : 'default'}
              variant={isOn ? 'filled' : 'outlined'}
              disabled={!allowed}
              title={allowed ? (v.description || v.name) : 'Not enabled at platform level — an admin must add it to the platform range first'}
            />
          );
        })}
      </Box>
      <FormHelperText>
        {helperText || 'Leave empty to inherit every voice the platform allows. Selecting voices narrows the choice for this group only.'}
        {outOfRange > 0 && ` ${outOfRange} voice${outOfRange === 1 ? ' is' : 's are'} greyed out because the platform does not currently offer ${outOfRange === 1 ? 'it' : 'them'}.`}
      </FormHelperText>
    </Box>
  );
};

export default VoiceWhitelist;
